import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import { MdArrowBack as ArrowBackIcon } from 'react-icons/md';
import { useNavigate, useLocation } from '@tanstack/react-router';
import type { TContentBlockBase } from '@onesource/schemas';
import DOMPurify from 'dompurify';

interface LocationState {
  block?: TContentBlockBase;
  lessonId?: string;
}

/**
 * Text Reader Page
 * Full-page text reader for TEXT content blocks
 */
export const TextReaderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | undefined;

  const block = state?.block;
  const lessonId = state?.lessonId;

  // Handle back navigation
  const handleBack = () => {
    if (lessonId) {
      navigate({ to: '/community-hub/lessons/$id', params: { id: lessonId } });
    } else {
      navigate({ to: '/community-hub' });
    }
  };

  // Loading state if no block data
  if (!block) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Content not available. Please navigate from a lesson.
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: '/community-hub' })}>
          Back to Community Hub
        </Button>
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, md: 3 }} maxWidth="800px" mx="auto">
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate({ to: '/community-hub' })}
          sx={{ cursor: 'pointer' }}
        >
          Community Hub
        </Link>
        {lessonId && (
          <Link component="button" variant="body2" onClick={handleBack} sx={{ cursor: 'pointer' }}>
            Lesson
          </Link>
        )}
        <Typography variant="body2" color="text.primary">
          Reading
        </Typography>
      </Breadcrumbs>
      {/* Content Header */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          {block.title || 'Reading'}
        </Typography>
      </Box>

      {/* Text Content */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          backgroundColor: 'grey.50',
          borderRadius: 2,
          mb: 4,
        }}
      >
        <Box
          sx={{
            typography: 'body1',
            lineHeight: 1.8,
            '& p': { mb: 2 },
            '& h1': { fontSize: '2rem', fontWeight: 600, mt: 4, mb: 2 },
            '& h2': { fontSize: '1.5rem', fontWeight: 600, mt: 3, mb: 2 },
            '& h3': { fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1.5 },
            '& ul, & ol': { pl: 3, mb: 2 },
            '& li': { mb: 1 },
            '& a': { color: 'primary.main' },
            '& blockquote': {
              borderLeft: 4,
              borderColor: 'primary.main',
              pl: 2,
              ml: 0,
              fontStyle: 'italic',
              color: 'text.secondary',
            },
            '& code': {
              backgroundColor: 'grey.200',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: '0.9em',
            },
            '& pre': {
              backgroundColor: 'grey.200',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              '& code': {
                backgroundColor: 'transparent',
                p: 0,
              },
            },
          }}
        >
          {block.bodyHtml ? (
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.bodyHtml) }} />
          ) : (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {block.body}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
